import * as yup from "yup";
import { TFunction } from "i18next";

/** Типы условий с парой «оператор + числовое значение». */
const NUMERIC_CONDITION_TYPES = [
  "posts",
  "annual_plan",
  "monthly_new_car_sales",
  "forecast_car_visits",
];

export const getRuleFormSchema = (t: TFunction) => {
  const required = () => t("validation.required");

  const selectValueRequired = () =>
    yup
      .object()
      .shape({
        value: yup.mixed().required(required()),
      })
      .nullable()
      .required(required());

  const multiSelectRequired = () =>
    yup
      .array()
      .of(selectValueRequired())
      .min(1, required())
      .required(required());

  const conditionSchema = yup.object().shape({
    conditionType: selectValueRequired(),
    postIds: yup.array().test("posts-required", required(), function (value) {
      if (this.parent?.conditionType?.value !== "posts") return true;
      return Array.isArray(value) && value.length > 0;
    }),
    operator: yup
      .object()
      .nullable()
      .test("operator-required", required(), function (value) {
        const type = this.parent?.conditionType?.value;
        if (!NUMERIC_CONDITION_TYPES.includes(type)) return true;
        return !!value && (value as { value?: unknown }).value != null;
      }),
    value: yup
      .mixed()
      .test("value-required", required(), function (value) {
        const type = this.parent?.conditionType?.value;
        if (!NUMERIC_CONDITION_TYPES.includes(type)) return true;
        return value !== "" && value != null;
      })
      .test("value-numeric", t("validation.integer"), function (value) {
        const type = this.parent?.conditionType?.value;
        if (!NUMERIC_CONDITION_TYPES.includes(type)) return true;
        if (value === "" || value == null) return true;
        return /^\d+$/.test(String(value).trim());
      }),
    category: yup
      .array()
      .test("category-required", required(), function (value) {
        if (this.parent?.conditionType?.value !== "category") return true;
        return Array.isArray(value) && value.length > 0;
      }),
  });

  const groupSchema = yup.object().shape({
    dealerIds: multiSelectRequired(),
    conditions: yup.array().of(conditionSchema),
  });

  return yup.object().shape({
    mainPositionId: selectValueRequired(),
    additionalPositionIds: multiSelectRequired(),
    rolePositionIds: yup.array().notRequired(),
    maxPerEmployee: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError(t("validation.numeric_field"))
      .integer(t("validation.integer"))
      .moreThan(0, t("validation.positive"))
      .notRequired(),
    groups: yup
      .array()
      .of(groupSchema)
      .required(required())
      .min(1, required())
      .test("unique-dealers", "", function (groups) {
        const seen = new Set<string>();
        const duplicatedNames: string[] = [];
        (groups ?? []).forEach((group) => {
          const dealerIds =
            (group?.dealerIds as { label: string; value: unknown }[]) ?? [];
          dealerIds.forEach((dealer) => {
            const key = String(dealer?.value);
            if (seen.has(key)) {
              if (!duplicatedNames.includes(dealer.label)) {
                duplicatedNames.push(dealer.label);
              }
            } else {
              seen.add(key);
            }
          });
        });
        if (duplicatedNames.length) {
          return this.createError({
            path: "groups",
            message: t("combinationRules.duplicate_dealers", {
              names: duplicatedNames.map((name) => `"${name}"`).join(", "),
            }),
          });
        }
        return true;
      }),
  });
};
